import { Controller } from "@hotwired/stimulus"
import { formatNumber, getCurrencyDecimals, getUserLocale } from "../../utils/number-format.util"
import { formatDecimal, serverNumberFormatFor } from "../../utils/server-format.util"

// Live preview for a cross-currency settlement row: derives the realized rate
// from the two amounts the user states, and — when the record carries a booking
// rate worth comparing against — the exchange difference between what the bank
// actually moved and what the record was booked at.
//
// Display only. Nothing here is persisted (spec D5): the rate stays derivable
// forever from the stored allocated + settled amounts, so there is no second
// source of truth to keep in sync.
export default class extends Controller {
  static targets = ["bankAmount", "documentAmount", "chargesAmount", "derived"]
  static values = {
    bookingRate: Number,
    bankCurrency: String,
    baseCurrency: String,
    documentCurrency: String,
    recordSettledOutsideBase: Boolean,
    referenceRate: Number,
    referenceDate: String
  }

  connect() {
    this.locale = getUserLocale()
    this.recalculate()
  }

  recalculate() {
    // The bank amount keeps its sign — it is what tells a payment from a
    // receipt, and the two read the same difference in opposite directions.
    // The rate itself is a magnitude either way.
    const signedBank = this.parseAmount(this.bankAmountTarget.value)
    const bank = Math.abs(signedBank)
    const settled = Math.abs(this.parseAmount(this.documentAmountTarget.value))
    // Bank charges are part of `settled` (the gross the record is discharged
    // by), so the bank's realised rate is on what actually ARRIVED.
    const charges = this.hasChargesAmountTarget ? Math.abs(this.parseAmount(this.chargesAmountTarget.value)) || 0 : 0
    const arrived = settled - charges

    if (!Number.isFinite(bank) || !Number.isFinite(settled) || bank <= 0 || settled <= 0 || !(arrived > 0)) {
      this.derivedTarget.hidden = true
      this.derivedTarget.replaceChildren()
      return
    }

    const parts = [this.line(this.derivedLabel("rate"), this.format(arrived / bank, 4))]

    if (charges > 0 && this.comparableToBookingRate) {
      const chargesBase = (charges * bank) / arrived
      parts.push(this.chargesLine(chargesBase, this.baseCurrencyValue, this.derivedLabel("currency")))
      const netGap = this.netGap(settled / this.bookingRateValue - bank, chargesBase, signedBank)
      parts.push(this.differenceLine(netGap, signedBank))
    } else if (charges > 0) {
      // No base value to state (the MCP summary withholds it for the same
      // reason), but the charges themselves are known — in the record's own
      // currency, exactly as typed.
      parts.push(this.chargesLine(charges, this.documentCurrencyValue, this.derivedLabel("documentCurrency")))
    } else if (this.comparableToBookingRate) {
      parts.push(this.differenceLine(settled / this.bookingRateValue - bank, signedBank))
    }

    if (this.hasReferenceRate) {
      const referenceLine = this.referenceLine(settled, bank, signedBank)
      if (referenceLine) parts.push(referenceLine)
    }

    this.derivedTarget.replaceChildren(...parts.filter(Boolean))
    this.derivedTarget.hidden = false
  }

  get hasReferenceRate() {
    return (
      this.hasReferenceRateValue &&
      this.referenceRateValue > 0 &&
      this.hasReferenceDateValue &&
      this.referenceDateValue !== ""
    )
  }

  // Every visible word comes from a server-rendered data-*-label attribute —
  // same contract as derivedLabel's other keys. A missing label means the
  // line is OMITTED, never rendered in a hardcoded language: returns null.
  // Outflow (a charge) reads drahšie/lacnejšie; inflow (a credit) has its own
  // referenceMoreIn/referenceLessIn keys, because "the bank asks more" is the
  // wrong sentence for money arriving.
  referenceLine(settled, bank, signedBank) {
    const refLabel = this.derivedLabel("reference")
    const asOf = this.derivedLabel("referenceAsOf")
    if (!refLabel || !asOf) return null

    const expectedBank = settled / this.referenceRateValue
    const spreadPct = (bank / expectedBank - 1) * 100
    const absSpread = Math.abs(spreadPct)

    let spreadText
    if (absSpread < 0.05) {
      spreadText = this.derivedLabel("referenceExact")
    } else {
      const outflow = signedBank < 0
      const key = spreadPct > 0 ? (outflow ? "referenceMore" : "referenceMoreIn") : (outflow ? "referenceLess" : "referenceLessIn")
      const template = this.derivedLabel(key)
      spreadText = template && template.replace("{spread}", this.format(absSpread, 1))
    }
    if (!spreadText) return null

    const wrapper = document.createElement("span")
    wrapper.className = "xfx__ref text-muted"
    wrapper.textContent = `${refLabel} ${this.format(this.referenceRateValue, 4)} ${asOf} ${this.referenceDateValue} · ${spreadText}`
    return wrapper
  }

  // Whether a difference can be stated at all. Two independent reasons it
  // cannot, and in both the derived rate stands alone rather than being joined
  // by a number with no honest meaning:
  //
  //  1. No booking rate. Exactly 1 is the "no rate known" sentinel the server
  //     already filters on (exchange_rate_valid?), so it never reaches the data
  //     attribute — with nothing to compare against there is no difference.
  //  2. Mismatched units. `bookingRate` converts the RECORD's currency into the
  //     record's BASE currency, while the bank field is in the TRANSACTION's
  //     currency, so `settled / bookingRate - bank` only has a unit when those
  //     two coincide. An EUR-base workspace settling a USD expense from a CZK
  //     account would otherwise print CZK subtracted from EUR — and the
  //     currency label on that line (the transaction's) would name a third
  //     thing again. Mirrors Transactionable#settled_in_base_currency?, which
  //     returns nil for the same reason rather than inventing a second rate
  //     nobody entered.
  //
  // Missing currency attributes fail closed: unknown units are not a licence to
  // subtract, so the difference is withheld exactly as a mismatch would be.
  get comparableToBookingRate() {
    if (!(this.bookingRateValue > 0) || this.bookingRateValue === 1) return false

    // The record may ALREADY carry a settled allocation from an account outside
    // its base currency. Transactionable#settlement_fx_difference is
    // all-or-nothing over the whole record and withholds the figure in that
    // case, so previewing one here would promise a number the summary refuses
    // to print the moment this pairing is saved. The preview is per-row; the
    // record is not.
    if (this.recordSettledOutsideBaseValue) return false

    const bankCurrency = this.bankCurrencyValue.toUpperCase()

    return bankCurrency !== "" && bankCurrency === this.baseCurrencyValue.toUpperCase()
  }

  // The exchange difference NET of bank charges, rounded the way the server
  // books it: the total gap and the charges each at the base currency's
  // decimals, then subtracted — so the preview and the summary agree to the
  // last minor unit (Transactionable#settlement_fx_difference). Charges only
  // exist on incoming money; the sign expression mirrors the server anyway.
  netGap(gap, chargesBase, signedBank) {
    const decimals = getCurrencyDecimals(this.baseCurrencyValue)
    const round = (value) => Math.round(value * 10 ** decimals) / 10 ** decimals
    return round(gap) - round(signedBank < 0 ? -chargesBase : chargesBase)
  }

  // Bank charges are always money that never arrived: a loss — in the base
  // currency at the rate the bank realised on this receipt (spec D4), or in the
  // record's own currency when no base value can be stated. Label from the server.
  chargesLine(amount, currency, currencyLabel) {
    const label = this.derivedLabel("charges")
    if (!label) return null
    const decimals = getCurrencyDecimals(currency)
    const value = `${this.format(-amount, decimals)} ${currencyLabel}`.trim()
    return this.line(label, value, "xfx__loss")
  }

  // `gap` is the record's booked EUR value minus the EUR the bank actually
  // moved. Whether that is a loss or a gain depends on which way the money
  // went, and nothing else:
  //
  //   OUTGOING (bank < 0), paid MORE than booked  (gap < 0)  -> LOSS
  //   OUTGOING (bank < 0), paid LESS than booked  (gap > 0)  -> GAIN
  //   INCOMING (bank > 0), received MORE          (gap < 0)  -> GAIN
  //   INCOMING (bank > 0), received LESS          (gap > 0)  -> LOSS
  //
  // i.e. it is a loss exactly when the gap and the movement point the same way.
  // The number is then re-signed by that verdict rather than by the raw
  // subtraction, so a loss always reads negative whichever side it is on — an
  // accountant reads the colour before the digits, and a receivable settled
  // above its booked value must not be painted as a loss.
  differenceLine(gap, signedBank) {
    // The difference is in the record's BASE currency — `settled / bookingRate`
    // is, and #comparableToBookingRate has already established the bank side
    // agrees — so its precision comes from that currency, never from a flat 2.
    // A VND-base row otherwise reported a 0,4 ₫ gain that cannot exist, and a
    // BHD one rounded a real 4-fils difference away to "0,00". Mirrors
    // Transactionable#settlement_fx_difference. See docs/VAT_ROUNDING_POLICY.md §2.
    const decimals = getCurrencyDecimals(this.baseCurrencyValue)
    const loss = gap < 0 === signedBank < 0
    const magnitude = Math.abs(gap)
    const value = `${this.format(loss ? -magnitude : magnitude, decimals)} ${this.derivedLabel("currency")}`.trim()

    // Below half a minor unit there is no direction to report — it rounds to
    // zero at the precision just printed, and gets neither treatment.
    const tone = magnitude < 0.5 * 10 ** -decimals ? null : loss ? "xfx__loss" : "xfx__gain"

    return this.line(this.derivedLabel("difference"), value, tone)
  }

  // Built as nodes rather than an HTML string: the values are localized numbers
  // and the labels come from the server, and neither has any business being
  // parsed as markup.
  line(label, value, tone = null) {
    const wrapper = document.createElement("span")
    wrapper.append(`${label} `)

    const strong = document.createElement(tone ? "span" : "b")
    if (tone) strong.className = tone
    strong.textContent = value
    wrapper.append(strong)

    return wrapper
  }

  // Printed beside server-rendered amounts, so replayed from the results list's
  // data-server-number-format (the user's regional format); Intl only when
  // the page embeds none.
  format(value, digits) {
    const digitOptions = { minimumFractionDigits: digits, maximumFractionDigits: digits }
    const numberFormat = serverNumberFormatFor(this.element)
    if (numberFormat) return formatDecimal(value, numberFormat, digitOptions)
    return formatNumber(value, this.locale, digitOptions)
  }

  // Both fields are `type=number`, whose `.value` is always a dot decimal
  // whatever the user's locale — so parseLocaleNumber would be actively wrong
  // here (in de-DE it reads "21.23" as 2123). The comma fallback covers a
  // browser that hands back what was typed on a comma keypad.
  parseAmount(value) {
    return Number.parseFloat(String(value).replace(",", "."))
  }

  // Labels are rendered server-side onto the element (data-rate-label,
  // data-difference-label, data-currency-label) so nothing user-visible is
  // hardcoded in English here.
  derivedLabel(key) {
    return this.element.dataset[`${key}Label`] || ""
  }
}
